import { CartItemModel, CourseModel } from './models';

export function moneyInCents(value: number): number {
  return Math.round(Number(value) * 100);
}

function validMoney(value: number | null | undefined): boolean {
  return value != null &&
    Number.isFinite(Number(value)) &&
    Number.isSafeInteger(moneyInCents(Number(value))) &&
    Math.abs(Number(value) - moneyInCents(Number(value)) / 100) < 0.00000001;
}

/**
 * Returns true when an admin pricing preview already contains the current/master
 * course as one of clusterCourses. Persisted/public cluster data does not contain
 * that self-reference; it carries the master's original price in individualPrice.
 */
function previewContainsMaster(course: CourseModel): boolean {
  const masterId = Number(course.courseId || 0);
  return masterId > 0 && (course.clusterCourses || []).some(unit => Number(unit.courseId) === masterId);
}

/**
 * Normal undiscounted price of the COMPLETE cluster.
 *
 * Public/persisted shape:
 *   master IndividualPrice + linked course prices
 *
 * Admin preview shape:
 *   clusterCourses already contains master + linked courses, so simply sum them
 *   and avoid counting the master twice.
 */
export function clusterNormalTotal(course: CourseModel): number {
  if (!course?.isCluster) return Number(course?.price || 0);

  const units = course.clusterCourses || [];
  const linkedTotalCents = units.reduce((sum, unit) => sum + moneyInCents(Number(unit.price || 0)), 0);

  if (previewContainsMaster(course)) {
    return linkedTotalCents / 100;
  }

  const masterPriceCents = moneyInCents(Number(course.individualPrice || 0));
  return (masterPriceCents + linkedTotalCents) / 100;
}

export function clusterSaving(course: CourseModel): number {
  if (!course?.isCluster || clusterValidationError(course)) return 0;
  return (moneyInCents(clusterNormalTotal(course)) - moneyInCents(Number(course.price || 0))) / 100;
}

export function clusterValidationError(course: CourseModel): string {
  if (!course?.isCluster) return '';

  const units = course.clusterCourses || [];
  if (units.length < 1) {
    return 'Select at least one linked course in addition to the current course.';
  }

  const ids = units.map(unit => Number(unit.courseId));
  if (ids.some(id => !Number.isInteger(id) || id <= 0) || new Set(ids).size !== ids.length) {
    return 'Select distinct linked courses.';
  }

  // Persisted/public data must not contain a self-reference. The admin editor can
  // intentionally build a preview containing the master course before save.
  const containsSelf = ids.includes(Number(course.courseId));
  const isAdminPreview = Number(course.clusterAnchorCourseId || 0) === Number(course.courseId || 0);
  if (containsSelf && !isAdminPreview) {
    return 'Select distinct units; a cluster cannot include itself.';
  }

  if (units.some(unit => !unit.isActive)) {
    return 'All included units must be active.';
  }

  const currency = String(course.currencyCode || '').trim().toUpperCase();
  if (units.some(unit => String(unit.currencyCode || '').trim().toUpperCase() !== currency)) {
    return 'All included units must use the cluster currency.';
  }

  if (units.some(unit => !validMoney(unit.price) || Number(unit.price) < 0)) {
    return 'Every included unit needs a valid individual price.';
  }

  // On public/persisted cluster data the master original price is required.
  if (!previewContainsMaster(course) && (!validMoney(course.individualPrice) || Number(course.individualPrice) <= 0)) {
    return 'The original individual course price is unavailable for this cluster.';
  }

  const normalTotal = clusterNormalTotal(course);
  if (!validMoney(course.price) || Number(course.price) <= 0 ||
      moneyInCents(Number(course.price)) >= moneyInCents(normalTotal)) {
    return 'Enter a cluster price greater than zero and lower than the normal total.';
  }

  return '';
}

/**
 * All courses represented by a cluster include the master/current course plus
 * every additional linked course. The Set removes duplicates for admin previews.
 */
export function includedCourseIds(course: CourseModel): number[] {
  if (!course?.isCluster) return [Number(course?.courseId)];

  return [...new Set([
    Number(course.courseId),
    ...(course.clusterCourses || []).map(unit => Number(unit.courseId))
  ].filter(id => Number.isInteger(id) && id > 0))];
}

// Offers are distinct by their own ID, even when two discounts cover the same units.
export function availableClusterOffers(courseId: number, offers: CourseModel[]): CourseModel[] {
  return [...new Map((offers || [])
    .filter(offer => offer?.isCluster && offer.isActive &&
      !clusterValidationError(offer) && includedCourseIds(offer).includes(Number(courseId)))
    .map(offer => [offer.courseId, offer])).values()]
    .sort((a, b) => a.price - b.price || a.courseName.localeCompare(b.courseName) || a.courseId - b.courseId);
}

export function coursesOverlap(first: CourseModel, second: CourseModel): boolean {
  const ids = new Set(includedCourseIds(first));
  return includedCourseIds(second).some(id => ids.has(id));
}

/** Build a cart line for the master/current course at its original individual price. */
function masterCartCourse(cluster: CourseModel): CourseModel {
  return {
    ...cluster,
    price: Number(cluster.individualPrice || 0),
    isCluster: false,
    clusterCourseIds: [],
    clusterCourses: [],
    availableClusters: []
  };
}

/**
 * Expands a complete cluster into master + linked course cart lines and allocates
 * the cluster discount proportionally across every included course.
 */
export function expandCluster(cluster: CourseModel): CartItemModel[] {
  const containsMaster = previewContainsMaster(cluster);

  const included = containsMaster
    ? (cluster.clusterCourses || []).map(unit => ({
        shortDescription: '', fullDescription: '', startDate: null, imageUrl: '', brochureUrl: '',
        tags: [], deliveryMode: '', locationName: '', courseTimes: [], ...unit, isCluster: false
      } as CourseModel))
    : [
        masterCartCourse(cluster),
        ...(cluster.clusterCourses || []).map(unit => ({
          shortDescription: '', fullDescription: '', startDate: null, imageUrl: '', brochureUrl: '',
          tags: [], deliveryMode: '', locationName: '', courseTimes: [], ...unit, isCluster: false
        } as CourseModel))
      ];

  const total = included.reduce((sum, item) => sum + moneyInCents(Number(item.price || 0)), 0);
  const discount = moneyInCents(clusterSaving(cluster));

  const allocations = included.map((item, index) => {
    const share = total ? discount * moneyInCents(Number(item.price || 0)) / total : 0;
    return { index, cents: Math.floor(share), remainder: share - Math.floor(share) };
  });

  let remaining = discount - allocations.reduce((sum, allocation) => sum + allocation.cents, 0);

  // Largest-remainder allocation keeps the sum exact to the cent.
  [...allocations]
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index)
    .forEach(allocation => {
      if (remaining > 0) {
        allocation.cents++;
        remaining--;
      }
    });

  return included.map((item, index) => ({
    course: item,
    quantity: 1,
    cluster,
    clusterDiscount: allocations[index].cents / 100
  }));
}

export function cartBookingItems(items: CartItemModel[]): CartItemModel[] {
  const seen = new Set<number>();
  return items.flatMap(item => {
    if (!item.cluster) return [item];
    if (seen.has(item.cluster.courseId)) return [];
    seen.add(item.cluster.courseId);
    return [{ course: item.cluster, quantity: 1 }];
  });
}

export function cartLinePrice(item: CartItemModel): number {
  return (moneyInCents(item.course.price) - moneyInCents(item.clusterDiscount || 0)) / 100;
}

export function cartSubtotal(items: CartItemModel[]): number {
  return items.reduce((sum, item) => sum + moneyInCents(cartLinePrice(item)), 0) / 100;
}

export function cartDiscount(items: CartItemModel[]): number {
  return items.reduce((sum, item) => sum + moneyInCents(item.clusterDiscount || 0), 0) / 100;
}
